import { request } from './api';

/**
 * Spec 085 (§3 Dealer portal) — typed client for `/api/dealers/me/**` (orders `DealerPortalController`)
 * plus the public catalog reads the book-on-behalf wizard needs. Shapes follow the backend records
 * (`DealerCreditViews`, `DealerBookingDtos`): credit money is a 2-dp string ("9500.00"); booking money is
 * a JSON number. Errors arrive as `ApiError` with `errorCode` = the backend `code` (FEATURE_OFF,
 * DEALER_SUSPENDED, COMMIT_NOT_ALLOWED, EXCEEDS_CREDIT, …).
 */

/** Realm role form, as `GET /api/dealers/me` answers `myRole`. */
export type DealerRealmRole = 'DEALER_OWNER' | 'DEALER_STAFF';
export type DealerStatus = 'DRAFT' | 'ACTIVE' | 'SUSPENDED';
export type CreditRequestStatus = 'PROFORMA_ISSUED' | 'AWAITING_PAYMENT' | 'PAID' | 'REVOKED';
export type CreditRequestKind = 'PER_BUYER' | 'PACK';
export type CreditStatus = 'PENDING' | 'LIVE' | 'EXPIRED' | 'REVOKED';
export type DocumentType = 'PROFORMA' | 'TAX_INVOICE' | 'CREDIT_NOTE';
export type LegMethod = 'GARAGAT' | 'SELF';
export type TransportType = 'RECOVERY' | 'ZOFEUR';

export interface CursorPage<T> {
  content: T[];
  nextCursor: string | null;
  hasMore: boolean;
  size: number;
}

export interface DealerView {
  id: string;
  legalName: string;
  tradeName: string;
  trn?: string;
  billingEmail?: string;
  discountPct?: string;
  paymentTermsDays?: number;
  status: DealerStatus;
  agreementOnFile: boolean;
  agreementSignedAt?: string;
}

export interface Showroom {
  id: string;
  dealerId?: string;
  dealerTradeName?: string;
  label: string;
  street?: string;
  city?: string;
  area?: string;
  emirate?: string;
  building?: string;
  latitude?: number;
  longitude?: number;
  instructions?: string;
  status?: string;
}

export interface DealerMe {
  dealer: DealerView;
  myRole: DealerRealmRole;
  canCommitSpend: boolean;
  agreementAccepted: boolean;
  showrooms?: Showroom[];
  /**
   * The dealer wallet switch (`garagat.dealer-credit.wallet.enabled`, default off, Maher 2026-11-03): credit packs,
   * the prepaid balance and assigning from it. Absent (older backend) = off.
   */
  walletEnabled?: boolean;
}

export interface DealerUser {
  id: string;
  subjectId: string;
  name: string;
  phone: string;
  email: string;
  /** Entity form: OWNER | STAFF (not the realm role). */
  role: 'OWNER' | 'STAFF';
  canCommitSpend: boolean;
  status: string;
  agreementAcceptedAt: string | null;
  createdAt: string;
}

export interface DealerDashboard {
  /** `null` while the wallet switch is off. */
  availableBalanceAed: string | null;
  liveCredits: number;
  liveFaceValueAed: string;
  liveUsedAed: string;
  liveRemainingAed: string;
  openRequests: number;
  pendingCredits: number;
}

export interface RequestQuote {
  faceValueAed: string;
  discountPct: string;
  payableAed: string;
  vatAed: string;
  totalDueAed: string;
  validityMonths: number;
}

export interface CreditRequest {
  id: string;
  dealerId: string;
  kind: CreditRequestKind;
  packId?: string;
  buyerCreditId?: string;
  number: string;
  faceValueAed: string;
  discountPct: string;
  payableAed: string;
  vatAed: string;
  totalDueAed: string;
  status: CreditRequestStatus;
  acceptedAt?: string;
  paidAt?: string;
  invoiceNumber?: string;
  revokedAt?: string;
  revokeReason?: string;
  note?: string;
  createdAt: string;
  proformaUrl?: string;
}

export interface CreditPack {
  id: string;
  name: string;
  faceValueAed: string;
  discountPct: string;
  appliedDiscountPct?: string;
  payableAed?: string;
  status: string;
}

export interface Lot {
  id: string;
  source: 'PACK' | 'RETURN' | 'REFUND';
  requestId?: string;
  faceValueAed: string;
  remainingAed: string;
  status: 'ACTIVE' | 'EXHAUSTED' | 'EXPIRED';
  expiresAt: string;
  createdAt: string;
}

export interface Balance {
  availableAed: string;
  lots: Lot[];
}

/** A credit as the issuing dealer sees it: first name + masked phone, totals only (BR-24). */
export interface Credit {
  id: string;
  dealerId: string;
  requestId?: string;
  hasAccount: boolean;
  buyerName?: string;
  buyerPhone?: string;
  faceValueAed: string;
  usedAed: string;
  reservedAed: string;
  remainingAed: string;
  expiredAed?: string;
  revokedAed?: string;
  status: CreditStatus;
  liveAt?: string;
  expiresAt?: string;
  note?: string;
  createdAt: string;
}

/** BR-8 / D-152 — `POST /customers/lookup`: the whole answer (never the email, phone, addresses or cars). */
export interface CustomerLookupResult {
  customerRef: string;
  displayName: string | null;
  garagatCustomer: true;
}

/** A customer the dealer found and confirmed, with the contacts the follow-up call must send again. */
export interface FoundCustomer {
  customerRef: string;
  displayName: string | null;
  email: string;
  phone: string;
}

export interface BuyerRow {
  customerId: string;
  firstName: string | null;
  maskedPhone: string;
  hasAccount: boolean;
  remainingAed: string;
  nextExpiry: string | null;
}

export interface BuyerVehicle {
  vehicleId: string;
  plateNumber?: string;
  make?: string;
  model?: string;
}

/** Same body as the app's vehicle create (users `CreateVehicleRequest`). */
export interface NewBuyerVehicle {
  label: string;
  make: string;
  model: string;
  year: number;
  plateNumber: string;
}

export interface DealerDocument {
  id: string;
  type: DocumentType;
  number: string;
  requestId?: string;
  amountAed?: string;
  vatAed?: string;
  status: string;
  createdAt: string;
  downloadUrl?: string;
}

/** `DealerBookingDtos.BookingRequest` — the quote and the booking take the same body. */
export interface DealerBookingRequest {
  buyerCustomerId: string;
  vehicleId: string;
  garageId: string;
  serviceOfferingId?: string;
  variantId?: string;
  variantIds?: string[];
  selectedVariationValueIds?: string[];
  scheduledDate: string;
  pickupMethod: LegMethod;
  returnMethod: LegMethod;
  pickupTimeSlotId?: string;
  returnTimeSlotId?: string;
  pickupShowroomId?: string;
  dropoffShowroomId?: string;
  transportType?: TransportType;
  notes?: string;
  expectedTotalPayable?: number;
  /** BR-8: required to book (the server re-runs the customer lookup against buyerCustomerId). */
  buyerEmail?: string;
  buyerPhone?: string;
}

export interface DealerBookingQuote {
  servicePrice: number | null;
  listPrice: number | null;
  deliveryFeeGross: number | null;
  serviceFeeGross: number | null;
  totalPayable: number;
  creditAvailableAed: number;
  creditAppliedAed: number;
  toPayNowAed: number;
  exceedsByAed: number | null;
}

export interface TimelineItem {
  type: string | null;
  at: string | null;
}

export interface DealerBooking {
  bookingId: string;
  status: string;
  buyerCustomerId: string;
  vehicleId: string;
  garageId: string;
  garageName: string | null;
  serviceName: string | null;
  scheduledDate: string;
  pickupTimeSlotId?: string | null;
  returnTimeSlotId?: string | null;
  pickupShowroomId?: string | null;
  dropoffShowroomId?: string | null;
  totalPayable: number | null;
  creditAppliedAed: number | null;
  toPayNowAed: number | null;
  orderId: string | null;
  orderNumber: string | null;
  orderStatus: string | null;
  orderVersion: number | null;
  createdAt: string;
  timeline: TimelineItem[] | null;
}

export interface RescheduleSlot {
  id: string;
  label: string;
  startTime?: string;
  endTime?: string;
}

export interface RescheduleOptions {
  allowed: boolean;
  reason?: string | null;
  reasonMessage?: string | null;
  phase?: string | null;
  editable?: string[];
  current?: {
    date?: string;
    pickupTimeSlotId?: string;
    returnDate?: string;
    returnTimeSlotId?: string;
    pickupMethod?: string;
    returnMethod?: string;
    version?: number;
  } | null;
  slots?: RescheduleSlot[];
  capacity?: { date: string; used: number; capacity: number | null; full: boolean } | null;
}

export interface RescheduleRequest {
  date?: string;
  pickupTimeSlotId?: string;
  returnDate?: string;
  returnTimeSlotId?: string;
  expectedVersion: number;
}

// ── public catalog (garages / platform) used by the booking wizard ──

export interface GarageSearchRow {
  id: string;
  name: string;
  city?: string;
  area?: string;
  averageRating?: number;
  minPrice?: number;
}

export interface GarageServiceRow {
  id: string;
  name: string;
  price?: number;
  durationMinutes?: number;
  category?: { id: string; name: string; nameAr?: string };
}

export interface VariationValue {
  id: string;
  label: string;
  isAvailableForVehicle?: boolean;
  quote?: { price?: number; durationMinutes?: number; resolvedVariantId?: string } | null;
}

export interface Variation {
  id: string;
  name: string;
  selectionMode?: string;
  values: VariationValue[];
}

export interface OfferingV2 {
  id: string;
  garageId: string;
  name: string;
  isCarSpecific?: boolean;
  flatPrice?: number | null;
  variations?: Variation[];
}

export interface ResolvedQuote {
  resolvedVariantId?: string | null;
  resolvedVariantIds?: string[] | null;
  price?: number | null;
  isAvailable: boolean;
  reason?: string;
}

export interface PickupTimeSlot {
  id: string;
  label: string;
  displayOrder?: number;
}

const BASE = '/api/dealers/me';

/** The dealer booking is priced with the service fee, like the app's checkout (`X-Garagat-Capabilities`). */
const CAPABILITIES = { 'X-Garagat-Capabilities': 'service-fee' };

function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const dealerApi = {
  me: (token: string) => request<DealerMe>({ method: 'GET', url: BASE, token }),
  acceptAgreement: (token: string) =>
    request<DealerUser>({ method: 'POST', url: `${BASE}/agreement/accept`, token }),
  dashboard: (token: string) => request<DealerDashboard>({ method: 'GET', url: `${BASE}/dashboard`, token }),

  // BR-8 / D-152 — step 1: find a registered customer by email AND phone
  lookupCustomer: (data: { email: string; phone: string }, token: string) =>
    request<CustomerLookupResult>({ method: 'POST', url: `${BASE}/customers/lookup`, data, token }),

  // Flow B — per-buyer credit requests
  quoteRequest: (faceValueAed: number, token: string) =>
    request<RequestQuote>({ method: 'POST', url: `${BASE}/credit-requests/quote`, data: { faceValueAed }, token }),
  createRequest: (data: { customerRef: string; buyerEmail: string; buyerPhone: string; faceValueAed: number; note?: string }, token: string) =>
    request<CreditRequest>({ method: 'POST', url: `${BASE}/credit-requests`, data, token }),
  listRequests: (token: string, opts: { status?: string; kind?: string; cursor?: string | null; limit?: number } = {}) =>
    request<CursorPage<CreditRequest>>({ method: 'GET', url: `${BASE}/credit-requests${qs(opts)}`, token }),
  getRequest: (id: string, token: string) =>
    request<CreditRequest>({ method: 'GET', url: `${BASE}/credit-requests/${id}`, token }),
  acceptRequest: (id: string, token: string) =>
    request<CreditRequest>({ method: 'POST', url: `${BASE}/credit-requests/${id}/accept`, token }),

  // Flow C — packs
  packs: (token: string) => request<CreditPack[]>({ method: 'GET', url: `${BASE}/packs`, token }),
  orderPack: (packId: string, token: string) =>
    request<CreditRequest>({ method: 'POST', url: `${BASE}/pack-orders`, data: { packId }, token }),
  acceptPackOrder: (id: string, token: string) =>
    request<CreditRequest>({ method: 'POST', url: `${BASE}/pack-orders/${id}/accept`, token }),

  // Flow D — balance and assignments
  balance: (token: string) => request<Balance>({ method: 'GET', url: `${BASE}/balance`, token }),
  assign: (data: { customerRef: string; buyerEmail: string; buyerPhone: string; amountAed: number; note?: string }, token: string) =>
    request<Credit>({ method: 'POST', url: `${BASE}/credits`, data, token }),
  listCredits: (token: string, opts: { status?: string; cursor?: string | null; limit?: number } = {}) =>
    request<CursorPage<Credit>>({ method: 'GET', url: `${BASE}/credits${qs(opts)}`, token }),

  // Buyers and their cars (G14)
  buyers: (token: string) => request<BuyerRow[]>({ method: 'GET', url: `${BASE}/buyers`, token }),
  buyerVehicles: (customerId: string, token: string) =>
    request<BuyerVehicle[]>({ method: 'GET', url: `${BASE}/buyers/${customerId}/vehicles`, token }),
  addBuyerVehicle: (customerId: string, data: NewBuyerVehicle, token: string) =>
    request<{ id: string; plateNumber?: string; make?: string; model?: string }>({
      method: 'POST', url: `${BASE}/buyers/${customerId}/vehicles`, data, token,
    }),

  // Flow H — bookings on behalf
  quoteBooking: (data: DealerBookingRequest, token: string) =>
    request<DealerBookingQuote>({ method: 'POST', url: `${BASE}/bookings/quote`, data, token, headers: CAPABILITIES }),
  book: (data: DealerBookingRequest, token: string) =>
    request<DealerBooking>({ method: 'POST', url: `${BASE}/bookings`, data, token, headers: CAPABILITIES }),
  completeWithCredit: (id: string, token: string) =>
    request<DealerBooking>({ method: 'POST', url: `${BASE}/bookings/${id}/complete-with-credit`, token }),
  listBookings: (token: string, opts: { cursor?: string | null; limit?: number } = {}) =>
    request<CursorPage<DealerBooking>>({ method: 'GET', url: `${BASE}/bookings${qs(opts)}`, token }),
  getBooking: (id: string, token: string) =>
    request<DealerBooking>({ method: 'GET', url: `${BASE}/bookings/${id}`, token }),
  cancelBooking: (id: string, token: string) =>
    request<DealerBooking>({ method: 'POST', url: `${BASE}/bookings/${id}/cancel`, token }),
  rescheduleOptions: (id: string, token: string, date?: string) =>
    request<RescheduleOptions>({ method: 'GET', url: `${BASE}/bookings/${id}/reschedule-options${qs({ date })}`, token }),
  reschedule: (id: string, data: RescheduleRequest, token: string) =>
    request<DealerBooking>({ method: 'POST', url: `${BASE}/bookings/${id}/reschedule`, data, token }),

  // Documents and users
  documents: (token: string, type?: DocumentType) =>
    request<DealerDocument[]>({ method: 'GET', url: `${BASE}/documents${qs({ type })}`, token }),
  users: (token: string) => request<DealerUser[]>({ method: 'GET', url: `${BASE}/users`, token }),
  setCommitSpend: (userId: string, canCommitSpend: boolean, token: string) =>
    request<DealerUser>({ method: 'PATCH', url: `${BASE}/users/${userId}`, data: { canCommitSpend }, token }),

  // Public catalog reads (any valid JWT; the same endpoints the app uses)
  searchGarages: (keyword: string, token: string) =>
    request<{ content: GarageSearchRow[] }>({
      method: 'GET', url: `/api/garages/search${qs({ keyword, page: 0, size: 20 })}`, token,
    }),
  garageServices: (garageId: string, token: string) =>
    request<GarageServiceRow[]>({ method: 'GET', url: `/api/garages/${garageId}/services`, token }),
  offering: (garageId: string, offeringId: string, vehicle: { make?: string; model?: string }, token: string) =>
    request<OfferingV2>({
      method: 'GET',
      url: `/api/garages/${garageId}/services-v2/${offeringId}${qs({ vehicleMake: vehicle.make, vehicleModel: vehicle.model })}`,
      token,
    }),
  resolveQuote: (garageId: string, offeringId: string, vehicle: { make?: string; model?: string },
    selectedVariationValueIds: string[], token: string) =>
    request<ResolvedQuote>({
      method: 'POST',
      url: `/api/garages/${garageId}/services-v2/${offeringId}/resolve-quote`,
      data: { vehicleSnapshot: { make: vehicle.make, model: vehicle.model }, selectedVariationValueIds },
      token,
    }),
  pickupTimeSlots: (token: string) =>
    request<PickupTimeSlot[]>({ method: 'GET', url: '/api/pickup-time-slots', token }),
};
